import { Link } from "@/lib/nav";

import FilterChips from "@/components/filter-chips";
import LinkTabs from "@/components/link-tabs";
import SectionCard from "@buildpy/ui/section-card";
import MetaSep from "@/components/meta-sep";
import { dateAfterDays, formatDateTime } from "@buildpy/core/lib/format";
import { DEFAULT_VAT_MODE } from "@buildpy/core/modules/money/vat";
import { DEFAULT_SHOW_COST, parseShowCost, SHOW_COST_COOKIE } from "@buildpy/core/modules/quote/editor/view-pref";
import { cookieOf } from "@/lib/cookie";
import ChangeOrderList from "@/modules/change-order/ui/change-order-list";
import Money from "@/components/money";
import CreateContractButton from "@/modules/contract/ui/create-contract-button";
import QuoteBody, { QuoteTotals } from "@/modules/quote/ui/detail/quote-body";
import SpaceSummary from "@/modules/quote/ui/detail/space-summary";
import QuoteActions from "@/modules/quote/ui/detail/quote-actions";
import QuoteProfit from "@/modules/quote/ui/detail/quote-profit";
import QuoteStatusBadge from "@/modules/quote/ui/status-badge";
import type { QuoteView } from "@buildpy/core/views/g1b/quote";
import { CrumbName } from "@/shell/crumb";
import PageHeader from "@/shell/page-header";

/**
 * 견적 상세. 발송 뒤에는 **고객이 본 스냅샷**을 그린다 —
 * 화면이 현재 행으로 다시 계산하면 「보낸 내용」 과 어긋난다.
 */
// 모르는 탭 값은 견적 내용으로 — 가견적엔 손익 탭이 없다.
const quoteTabOf = (tab: string | undefined, kind: string, canView: boolean): "content" | "profit" =>
  tab === "profit" && kind !== "ROUGH" && canView ? "profit" : "content";

// 실제 원가를 못 고치는 까닭 — 지난 버전이 먼저(대표도 못 고친다).
const profitLockOf = (view: QuoteView): string | null =>
  !view.isLatestVersion
    ? "지난 버전의 실제 원가는 볼 수만 있습니다 — 최신 버전에서 입력하세요"
    : !view.profitAccess.edit
      ? "실제 원가는 대표만 고칠 수 있습니다 — 회사 설정 직원 권한에서 바꿀 수 있습니다"
      : null;

const Page = ({ data: view, tab }: { data: QuoteView; tab?: string }) => {
  const { quote, customer, versions, activities, share, validDays, spaceOptions, contract, changeOrders, current, data, cost, profit } = view;
  const contentHref = `/app/quotes/${quote.id}`;
  const profitHref = `${contentHref}?tab=profit`;
  const activeTab = quoteTabOf(tab, quote.kind, view.profitAccess.view);
  // 원가·마진 보기 — 편집기에서 끈 사람은 상세에서도 안 보인다(같은 쿠키).
  const showCost = parseShowCost(cookieOf(SHOW_COST_COOKIE)) ?? DEFAULT_SHOW_COST;

  return (
    <>
      <CrumbName name={`${quote.quote_no} v${quote.version}`} />
      <PageHeader
        actions={
          <QuoteActions
            defaultExpiresDate={dateAfterDays(validDays)}
            defaultLast4={view.defaultLast4}
            quoteId={quote.id}
            isLatest={versions.every(
              // 아직 안 보낸 초안은 「더 최신」 으로 치지 않는다 — v2 초안을 떠 둔
              // 채로는 v1 의 수주·이탈을 기록할 길이 없어진다.
              (v) => v.status === "DRAFT" || v.version <= quote.version
            )}
            shareExpiresAt={share?.expires_at ?? null}
            status={quote.status}
            token={share?.token ?? null}
            viewCount={share?.view_count ?? 0}
            vatMode={quote.vat_mode ?? DEFAULT_VAT_MODE}
            viewedAt={share?.viewed_at ?? null}
          />
        }
        meta={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-3 md:gap-x-2 md:gap-y-1">
            <QuoteStatusBadge status={quote.status} />
            {quote.kind === "ROUGH" && (
              <span className="rounded-md bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                가견적
              </span>
            )}
            <span>{quote.title}</span>
            <MetaSep />
            <Link
              className="tap-target relative underline-offset-2 hover:underline"
              href={`/app/customers/${customer.id}`}>
              {customer.name}
            </Link>
            <MetaSep />
            <b className="font-semibold tabular-nums text-foreground">
              <Money value={quote.total} />
            </b>
          </span>
        }
        title={`${quote.quote_no} v${quote.version}`}
      />

      {versions.length > 1 && (
        <FilterChips
          activeHref={`/app/quotes/${quote.id}`}
          aria-label="견적 버전"
          items={versions.map((version) => ({
            href: `/app/quotes/${version.id}`,
            label: (
              <>
                v{version.version}
                {version.id !== quote.id && <QuoteStatusBadge status={version.status} />}
              </>
            ),
          }))}
        />
      )}

      {(quote.status === "WON" || quote.status === "LOST") && (
        <SectionCard className="text-sm" padding="sm">
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <span className="text-muted-foreground">
              {quote.status === "WON" ? "수주일" : "이탈일"}
            </span>
            <span className="tabular-nums">
              {formatDateTime(quote.decided_at)}
            </span>
            {quote.status === "LOST" && (
              <>
                <span className="text-muted-foreground">사유</span>
                <span>{quote.lost_reason || "—"}</span>
              </>
            )}
            {quote.status === "WON" && (
              <span className="ml-auto" data-testid="quote-contract">
                {contract ? (
                  <Link
                    className="font-medium underline-offset-2 hover:underline"
                    href={`/app/contracts/${contract.id}`}>
                    계약서 보기 ({contract.contract_no})
                  </Link>
                ) : (
                  <CreateContractButton quoteId={quote.id} />
                )}
              </span>
            )}
          </div>
        </SectionCard>
      )}

      {contract && changeOrders && current && (
        <SectionCard className="flex flex-col gap-y-2 text-sm" padding="sm" data-testid="quote-change-history">
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <h2 className="text-sm font-semibold">변경 이력</h2>
            <span className="text-muted-foreground">계약금액</span>
            <Money value={current.original} />
            <span className="text-muted-foreground">변경 합계</span>
            <Money signed value={current.accepted_delta} />
            <span className="text-muted-foreground">현재 계약금액</span>
            <span className="font-semibold tabular-nums" data-testid="quote-current-total">
              <Money value={current.current} />
            </span>
          </div>
          <p className="text-xs text-muted-foreground">
            견적은 그대로 두고, 체결 뒤 바뀐 금액은 계약의 변경공사로 관리합니다.
          </p>
          <div className="overflow-x-auto">
            <ChangeOrderList changeOrders={changeOrders} testId="quote-change-order-list" />
          </div>
        </SectionCard>
      )}

      {quote.kind !== "ROUGH" && view.profitAccess.view && (
        <LinkTabs
          activeHref={activeTab === "profit" ? profitHref : contentHref}
          aria-label="견적 탭"
          items={[
            { label: "견적 내용", href: contentHref },
            { label: "손익 (내부)", href: profitHref },
          ]}
        />
      )}

      {activeTab === "profit" ? (
        <QuoteProfit key={quote.id} lockedReason={profitLockOf(view)} profit={profit} quoteId={quote.id} />
      ) : (
        /* lg 오른쪽 칸 280 — 1100 폭에서 견적 내역 표(523)가 왼쪽 칸에 넘치지 않게 한다. */
        <div className="grid gap-4 lg:grid-cols-[1fr_280px] xl:grid-cols-[1fr_340px]">
          <div className="flex min-w-0 flex-col gap-y-4">
            <QuoteBody data={data} />
          </div>

          <div className="flex flex-col gap-y-4">
            <QuoteTotals cost={cost} data={data} showCost={showCost} />

            <SpaceSummary data={data} spaces={spaceOptions} />

            <SectionCard padding="sm">
              <h2 className="mb-2 text-sm font-semibold">이력</h2>
              {activities.length === 0 ? (
                <p className="text-sm text-muted-foreground">기록이 없습니다.</p>
              ) : (
                <ol className="flex flex-col gap-y-2">
                  {activities.map((activity) => (
                    <li key={activity.id} className="flex flex-col gap-y-0.5">
                      <span className="text-sm">{activity.summary}</span>
                      <span className="text-xs tabular-nums text-muted-foreground">
                        {formatDateTime(activity.created_at)}
                      </span>
                    </li>
                  ))}
                </ol>
              )}
            </SectionCard>
          </div>
        </div>
      )}
    </>
  );
};

export default Page;
